import _ from 'lodash';
import { persistSettingsDraft } from './settings_draft';
import {
  lightCatalogErrors,
  lightCycle,
  lightLayoutKnown,
  lightStorageAvailable,
  supportsLightChannels
} from './light_profiles';

export const LIGHT_SUBMISSION_TIMEOUT_MS = 60000;

export function lightDraftSubmission(controller) {
  const submission = controller.light_submission;
  if (submission?.status === 'pending') return submission;
  if (controller.light_save_error) return { status: controller.light_save_error };
  if (['rejected', 'unconfirmed'].includes(submission?.status)) return submission;
  const retained = controller.temp_settings?.light_channels;
  const baseline = controller.settings?.light_channels;
  return retained && baseline && retained.revision !== baseline.revision
    ? { status: 'conflict' } : submission;
}

export function discardLightDraft(controller) {
  delete controller.temp_settings.light_channels;
  // Discard removes later edits, but cannot cancel a command already sent.
  // Keep pending confirmation so its readback is not treated as a remote edit.
  if (controller.light_submission?.status !== 'pending') delete controller.light_submission;
  delete controller.light_save_error;
  persistSettingsDraft(controller);
  return true;
}

export function keepLightDraft(controller) {
  if (controller.light_submission?.status === 'pending') return false;
  const baseline = controller.settings.light_channels;
  if (lightCatalogErrors(baseline, {}, baseline).length ||
      controller.settings.light_channels_info?.revision !== baseline.revision) return false;
  if (controller.temp_settings.light_channels)
    controller.temp_settings.light_channels.revision = baseline.revision;
  controller.light_submission = { status: 'review' };
  delete controller.light_save_error;
  persistSettingsDraft(controller);
  return true;
}

export function expireLightSubmission(controller, now = Date.now()) {
  const pending = controller.light_submission;
  if (pending?.status === 'pending' && now - pending.started_at >= LIGHT_SUBMISSION_TIMEOUT_MS)
    pending.status = 'unconfirmed';
}

export function lightSubmissionMessage(submission) {
  if (submission.status === 'rejected') {
    if (['busy', 'queue_full'].includes(submission.reason)) return 'lighting_save_busy';
    if (['awaiting_readback', 'missing_readback'].includes(submission.reason)) return 'lighting_save_loading';
  }
  return `lighting_save_${submission.status === 'unsent' ? 'failed' : submission.status}`;
}

// The submitted snapshot is already active. Only later, unsent changes need a
// Restore/Discard choice when another lighting page opens during its save.
export const isPendingLightCatalog = (controller) =>
  controller.light_submission?.status === 'pending' &&
  Boolean(controller.temp_settings.light_channels) &&
  _.isEqual(controller.temp_settings.light_channels, controller.light_submission.submitted);

export const lightCatalogNeedsRecovery = (controller) => {
  const pending = controller.light_submission;
  // An edit may undo an in-flight change back to current readback. It still
  // differs from what was sent and must remain eligible for Restore/Discard.
  const baseline = pending?.status === 'pending' ? pending.submitted : controller.settings.light_channels;
  return Boolean(controller.temp_settings.light_channels) &&
    !_.isEqual(controller.temp_settings.light_channels, baseline);
};

export function lightCatalogPayload(catalog) {
  return {
    schema_version: catalog.schema_version,
    revision: catalog.revision,
    advanced_zone_mask: catalog.advanced_zone_mask,
    zones: catalog.zones.map(({ zone, master, enabled, shared_toplight }) => ({
      zone,
      master,
      enabled,
      shared_toplight
    })),
    subzones: catalog.subzones.map(
      ({ zone, subzone, master, enabled, enabled_mask, visible_mask, main_channel }) => ({
        zone,
        subzone,
        master,
        enabled,
        enabled_mask,
        visible_mask,
        main_channel
      })
    ),
    channels: catalog.channels.map(
      ({ zone, subzone, channel, name, minimum, maximum, points }) => ({
        zone,
        subzone,
        channel,
        name: name || '',
        minimum,
        maximum,
        points: (points || []).map(({ offset_minutes, level, anchor, interpolation }) => ({
          offset_minutes,
          level,
          anchor,
          interpolation
        }))
      })
    )
  };
}

function comparable(catalog) {
  const value = lightCatalogPayload(catalog);
  delete value.revision;
  value.zones.sort((a, b) => a.zone - b.zone);
  value.subzones.sort((a, b) => a.zone - b.zone || a.subzone - b.subzone);
  value.channels.sort((a, b) => a.zone - b.zone || a.subzone - b.subzone || a.channel - b.channel);
  return value;
}

export function submitLightChannels(controller, catalog, wsManager, now = Date.now()) {
  const info = controller.settings?.light_channels_info;
  if (!lightStorageAvailable(info) || !lightLayoutKnown(info)) return false;
  const baseline = controller.settings?.light_channels;
  if (lightCatalogErrors(baseline, {}, baseline).length) return false;
  if (
    controller.settings?.light_channels_info?.revision !==
    controller.settings?.light_channels?.revision
  )
    return false;
  if (
    !supportsLightChannels(controller) ||
    !controller.access_rights?.lights?.write ||
    controller.connection?.status === 'bad'
  )
    return false;
  if (controller.light_submission?.status === 'pending') return false;
  if (
    lightCatalogErrors(
      catalog,
      Object.fromEntries(
        Object.values(controller.zones ?? {}).map((z) => [z.id, lightCycle(z.settings?.timing)])
      ),
      controller.settings.light_channels
    ).length
  )
    return false;
  const base = Number(controller.settings.light_channels.revision);
  // A draft is based on a global snapshot: refuse to overwrite another zone's edits.
  if (Number(catalog.revision) !== base) return false;
  const wire = { ...lightCatalogPayload(catalog), base_revision: base };
  try {
    if (
      !wsManager.sendWSMsg(
        controller,
        'update_setpoints',
        { id: '254', light_channels: wire },
        controller.type === 6000,
        true
      )
    )
      return false;
  } catch (_) {
    return false;
  }
  controller.temp_settings.light_channels = _.cloneDeep(catalog);
  delete controller.light_save_error;
  persistSettingsDraft(controller);
  controller.light_submission = {
    status: 'pending',
    base_revision: base,
    expected_revision: (base + 1) >>> 0 || 1,
    started_at: now,
    submitted: _.cloneDeep(catalog)
  };
  return true;
}

export function reconcileLightChannels(controller, data) {
  if (data.light_channels_info)
    controller.settings.light_channels_info = _.cloneDeep(data.light_channels_info);
  if (!data.light_channels) return;
  const actual = data.light_channels;
  controller.settings.light_channels = _.cloneDeep(actual);
  const pending = controller.light_submission;
  if (!pending || !['pending', 'unconfirmed', 'conflict', 'rejected'].includes(pending.status))
    return;
  if (actual.revision === pending.base_revision) return;
  // Failure broadcasts identify only the base revision, which concurrent clients
  // can share. Authoritative catalog content takes precedence over that failure.
  delete pending.reason;
  delete controller.light_save_error;
  if (
    actual.revision !== pending.expected_revision ||
    !_.isEqual(comparable(actual), comparable(pending.submitted))
  ) {
    pending.status = 'conflict';
    return;
  }
  pending.status = 'confirmed';
  // A remote whole-zone copy supersedes its pre-copy ordinary draft. Retire
  // that exact snapshot before deleting the copy metadata; later edits survive.
  for (const [zone, beforeCopy] of Object.entries(pending.submitted._zone_lights_prior_drafts ?? {})) {
    const destination = controller.zones?.[zone];
    if (destination?.temp_settings?.lights && _.isEqual(destination.temp_settings.lights, beforeCopy)) {
      delete destination.temp_settings.lights;
      persistSettingsDraft(destination);
    }
  }
  if (_.isEqual(controller.temp_settings.light_channels, pending.submitted)) {
    delete controller.temp_settings.light_channels;
  } else if (controller.temp_settings.light_channels) {
    // New edits made after submission retain their content on the confirmed base.
    controller.temp_settings.light_channels.revision = actual.revision;
  }
  persistSettingsDraft(controller);
}

export function rejectLightChannels(controller, data) {
  const parts = data?.message_parts;
  if (parts?.[0] !== 'light_channels_upload_failed') return false;
  const pending = controller?.light_submission;
  if (
    pending &&
    ['pending', 'unconfirmed', 'rejected'].includes(pending.status) &&
    parts[1] === '254' &&
    Number(parts[2]) === pending.base_revision
  ) {
    pending.status = 'rejected';
    pending.reason = parts[3];
  }
  return true;
}
